import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State } from '@stencil/core'
import { clamp } from 'lodash-es'

import { classes } from '../../utils/css'

@Component({
  tag: 'ui-scrollbar',
})
export class ScrollBar {
  @Element() el: HTMLUiScrollbarElement
  @Event() updated: EventEmitter<number>

  @Prop() axis: 'x' | 'y'
  @Prop() contentPct = 0.5
  @Prop() scrollPct = 0

  @State() active: boolean

  mouseOffsetX: number
  mouseOffsetY: number
  thumbEl: HTMLButtonElement
  thumbRect: DOMRect
  trackRect: DOMRect

  get clampedContentPct() {
    return clamp(this.contentPct, 0.1, 0.75)
  }

  @Listen('mousedown')
  startDrag(e: MouseEvent) {
    this.trackRect = this.el.getBoundingClientRect()
    this.thumbRect = this.thumbEl.getBoundingClientRect()
    if (e.target === this.thumbEl) {
      this.mouseOffsetX = e.clientX - this.thumbRect.left
      this.mouseOffsetY = e.clientY - this.thumbRect.top
      this.active = true
    } else {
      this.mouseOffsetX = this.thumbRect.width / 2
      this.mouseOffsetY = this.thumbRect.height / 2
    }
  }

  @Listen('click')
  onClick(e: MouseEvent) {
    if (e.target === this.el) {
      this.update(e.clientX, e.clientY)
    }
  }

  @Listen('mouseup', { target: 'body', capture: true, passive: true })
  stopDrag() {
    this.active = false
  }

  @Listen('mousemove', { target: 'body', passive: true, capture: true })
  onDrag(e: MouseEvent) {
    if (this.active) {
      this.update(e.clientX, e.clientY)
    }
  }

  update(mouseX: number, mouseY: number, _tween = false) {
    let distTotal: number
    let distCurrent: number
    if (this.axis === 'x') {
      distTotal = this.trackRect.width - this.thumbRect.width
      distCurrent = mouseX - this.mouseOffsetX - this.trackRect.left
    } else {
      distTotal = this.trackRect.height - this.thumbRect.height
      distCurrent = mouseY - this.mouseOffsetY - this.trackRect.top
    }
    const pct = distCurrent / distTotal
    if (!isNaN(pct) && distTotal > 0) {
      this.updated.emit(clamp(pct, 0, 1))
    }
  }

  disconnectedCallback() {
    this.active = false
  }

  render() {
    return (
      <Host
        hidden={this.contentPct >= 1}
        class={`
          block overflow-hidden
          ${classes(this.axis === 'x', 'h-2', 'w-2')}
        `}
      >
        <button
          type='button'
          ref={(el) => (this.thumbEl = el)}
          class={`
            absolute top-0 left-0 flex border border-surface-base bg-surface-higher rounded-sm
            ${classes(this.axis === 'x', 'h-full', 'w-full')}
          `}
          style={{
            outline: 'none',
            [this.axis === 'x' ? 'width' : 'height']: this.clampedContentPct * 100 + '%',
            [this.axis === 'x' ? 'left' : 'top']: (1 - this.clampedContentPct) * this.scrollPct * 100 + '%',
          }}
        ></button>
      </Host>
    )
  }
}
